import { Component, Inject, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { OfflineSyncService } from '../../../Services/offline/offline-sync.service';
import {
  KonfliktEntscheidung,
  SyncKonfliktEintrag,
  SyncKonfliktFeld,
  SyncQueueEintrag,
  SyncTyp
} from '../../../Models/offline.model';

/** Optionale Eingrenzung: nur Konflikte eines Projekts zeigen. */
export interface SyncKonfliktDialogData {
  projektId?: number | null;
}

/** Was der Dialog beim Schließen zurückgeben kann. */
export interface SyncKonfliktDialogErgebnis {
  /** Der Benutzer möchte dieser Messstelle einen anderen QR-Code geben – bitte dorthin navigieren. */
  navigiereZuMessstelle?: number;
}

/** Eine Zeile der Gegenüberstellung. */
interface Vergleichszeile {
  schluessel: string;
  bezeichnung: string;
  lokal: string;
  server: string;
}

/**
 * Zwei Leute haben denselben Datensatz bearbeitet – wer gewinnt?
 *
 * Julia lädt eine Messstelle auf Stand 4 herunter und fährt auf die Anlage.
 * Max ändert sie online, der Server steht danach auf Stand 5. Julia
 * synchronisiert abends ihre Fassung, die noch auf Stand 4 beruht.
 *
 * Der Server hat sie deshalb NICHT angewendet, sondern gemeldet. Hier
 * entscheidet ein Mensch, welche Fassung gilt – Konflikt für Konflikt, bei
 * mehreren Feldern auch je Feld. Jede Schaltfläche sagt vorher, was
 * übernommen und was verworfen wird. „Später entscheiden" lässt den Konflikt
 * offen; nichts verschwindet stillschweigend.
 *
 * QR-Konflikte sind anders: ein Sticker kann nur an einer Messstelle hängen.
 * Deshalb gibt es dort kein „meine Fassung durchsetzen", sondern nur sichere
 * Auswege – Zuordnung zurücknehmen, anderen Code scannen oder den vorhandenen
 * Code der Messstelle ersetzen.
 */
@Component({
  selector: 'app-sync-konflikt-dialog',
  templateUrl: './sync-konflikt-dialog.component.html',
  styleUrls: ['./sync-konflikt-dialog.component.css']
})
export class SyncKonfliktDialogComponent implements OnInit {

  konflikte: SyncKonfliktEintrag[] = [];

  /**
   * Änderungen, die der Server endgültig abgelehnt hat – fachlich falsch,
   * ohne Berechtigung oder auf einen inzwischen gelöschten Datensatz. Sie
   * bleiben sichtbar, damit der Benutzer weiß, was NICHT angekommen ist.
   */
  fehlgeschlagen: SyncQueueEintrag[] = [];

  isLoading = true;

  /** Welcher Konflikt gerade angezeigt wird. */
  index = 0;

  /** Eine Entscheidung läuft gerade – gegen Doppelklicks. */
  laufend = false;

  /** Feldweise Auswahl für den aktuellen Konflikt: Schlüssel → Fassung. */
  feldwahl: { [schluessel: string]: 'lokal' | 'server' } = {};

  private readonly projektId: number | null;

  constructor(
    private sync: OfflineSyncService,
    private dialogRef: MatDialogRef<SyncKonfliktDialogComponent, SyncKonfliktDialogErgebnis | undefined>,
    @Optional() @Inject(MAT_DIALOG_DATA) data: SyncKonfliktDialogData | null
  ) {
    this.projektId = data?.projektId ?? null;
  }

  ngOnInit(): void {
    void this.laden();
  }

  private async laden(): Promise<void> {
    this.isLoading = true;

    const alle = await this.sync.ladeKonflikte();
    const abgelehnt = await this.sync.ladeFehlgeschlagene();

    this.konflikte = this.projektId == null
      ? alle
      : alle.filter(k => k.projektId === this.projektId);

    this.fehlgeschlagen = this.projektId == null
      ? abgelehnt
      : abgelehnt.filter(f => f.projektId === this.projektId);

    this.index = Math.min(this.index, Math.max(0, this.konflikte.length - 1));
    this.feldwahlZuruecksetzen();
    this.isLoading = false;

    if (this.konflikte.length === 0 && this.fehlgeschlagen.length === 0) {
      this.dialogRef.close();
    }
  }

  // ===================================================================
  // AKTUELLER KONFLIKT
  // ===================================================================

  get aktuell(): SyncKonfliktEintrag | null {
    return this.konflikte[this.index] ?? null;
  }

  get istQr(): boolean {
    return this.aktuell?.art === 'QR';
  }

  /** Mehrere strittige Felder: der Benutzer darf je Feld entscheiden. */
  get feldweiseMoeglich(): boolean {
    return !this.istQr && (this.aktuell?.felder?.length ?? 0) > 1;
  }

  /** Die Gegenüberstellung – je strittigem Feld eine Zeile, sonst die Gesamtfassung. */
  get zeilen(): Vergleichszeile[] {
    const k = this.aktuell;

    if (!k) {
      return [];
    }

    if (k.felder && k.felder.length > 0) {
      return k.felder.map(f => ({
        schluessel: f.schluessel,
        bezeichnung: f.bezeichnung,
        lokal: f.lokalText,
        server: f.serverText
      }));
    }

    return [{ schluessel: '*', bezeichnung: k.feld, lokal: k.lokalText, server: k.serverText }];
  }

  /** Worum es geht: „Messstelle 10PAB10CP001" bzw. „SPS-Signal 07-BMA". */
  get betroffenesObjekt(): string {
    const k = this.aktuell;

    if (!k) {
      return '';
    }

    switch (k.typ) {
      case 'SPS_PRUEFSTATUS':
        return `SPS-Signal ${k.bezeichnung}`;

      case 'PRUEFPUNKT_ERGEBNIS':
      case 'PRUEFPUNKT_LOESCHEN':
      case 'PRUEFPUNKT_ANLEGEN':
        return `Prüfpunkt „${k.bezeichnung}"`;

      default:
        return `Messstelle ${k.bezeichnung}`;
    }
  }

  /** Die Erklärung über der Gegenüberstellung. */
  get erklaerung(): string {
    const k = this.aktuell;

    if (!k) {
      return '';
    }

    if (k.art === 'QR') {
      return k.qr?.code === 'messstelle_has_qr'
        ? 'Diese Messstelle trägt am Server bereits einen anderen QR-Code. Ein zweiter Code kann nicht zusätzlich vergeben werden.'
        : 'Dieser QR-Code wurde am Server inzwischen einer anderen Messstelle zugeordnet. Ein Code kann nur an einer Messstelle hängen – die Zuordnung konnte deshalb nicht übertragen werden.';
    }

    if (k.typ === 'PRUEFPUNKT_LOESCHEN') {
      return 'Du hast diesen Prüfpunkt offline gelöscht, aber er wurde am Server inzwischen bearbeitet. Bitte entscheide, ob er gelöscht werden soll oder erhalten bleibt.';
    }

    const was = this.feldweiseMoeglich ? 'Diese Felder wurden' : 'Dieses Feld wurde';

    return `${was} sowohl auf deinem Gerät als auch am Server geändert. Bitte entscheide, welche Version übernommen werden soll.`;
  }

  /** Was passiert, wenn „Meine Änderung übernehmen" gedrückt wird. */
  get folgeMeine(): string {
    const z = this.zeilen;

    if (z.length === 1) {
      return `Dein Wert „${z[0].lokal}" wird an den Server übertragen und ersetzt dort „${z[0].server}".`;
    }

    return 'Alle deine Werte werden an den Server übertragen und ersetzen dort die aktuellen Werte.';
  }

  /** Was passiert, wenn „Serverversion übernehmen" gedrückt wird. */
  get folgeServer(): string {
    const z = this.zeilen;

    if (this.aktuell?.typ === 'PRUEFPUNKT_LOESCHEN') {
      return 'Der Prüfpunkt bleibt erhalten; deine Löschung wird verworfen.';
    }

    if (z.length === 1) {
      return `Der Serverwert „${z[0].server}" wird auf dieses Gerät übernommen; dein Wert „${z[0].lokal}" wird verworfen.`;
    }

    return 'Alle Serverwerte werden auf dieses Gerät übernommen; deine Werte werden verworfen.';
  }

  /** Bei feldweiser Auswahl: was mit der aktuellen Auswahl geschieht. */
  get folgeAuswahl(): string {
    const uebernommen: string[] = [];
    const verworfen: string[] = [];

    for (const z of this.zeilen) {
      if (this.feldwahl[z.schluessel] === 'server') {
        uebernommen.push(`${z.bezeichnung}: „${z.server}" (Server)`);
        verworfen.push(`${z.bezeichnung}: „${z.lokal}" (deine Änderung)`);
      } else {
        uebernommen.push(`${z.bezeichnung}: „${z.lokal}" (deine Änderung)`);
        verworfen.push(`${z.bezeichnung}: „${z.server}" (Server)`);
      }
    }

    return `Übernommen wird ${uebernommen.join(', ')}. Verworfen wird ${verworfen.join(', ')}.`;
  }

  get kurzerToken(): string {
    const t = this.aktuell?.qr?.token ?? '';
    return t.length > 16 ? `${t.slice(0, 8)}…${t.slice(-6)}` : t;
  }

  get kurzerVorhandenerToken(): string {
    const t = this.aktuell?.qr?.vorhandenerToken ?? '';
    return t.length > 16 ? `${t.slice(0, 8)}…${t.slice(-6)}` : t;
  }

  waehle(schluessel: string, fassung: 'lokal' | 'server'): void {
    this.feldwahl = { ...this.feldwahl, [schluessel]: fassung };
  }

  private feldwahlZuruecksetzen(): void {
    this.feldwahl = {};

    for (const z of this.zeilen) {
      this.feldwahl[z.schluessel] = 'lokal';
    }
  }

  // ===================================================================
  // ENTSCHEIDUNGEN
  // ===================================================================

  /** Die eigene, offline erfasste Fassung gilt – für alle strittigen Felder. */
  async meineAenderung(): Promise<void> {
    await this.entscheide(k => this.sync.uebernehmeLokaleFassung(k.id));
  }

  /** Die Serverfassung gilt – die Serverwerte werden auf dieses Gerät übernommen. */
  async serverversion(): Promise<void> {
    await this.entscheide(k => this.sync.uebernehmeServerfassung(k.id));
  }

  /** Feldweise Auswahl übernehmen. */
  async auswahlUebernehmen(): Promise<void> {
    const entscheidung: KonfliktEntscheidung = { gesamt: 'lokal', feldweise: { ...this.feldwahl } };
    await this.entscheide(k => this.sync.loeseKonflikt(k.id, entscheidung));
  }

  /** Der Konflikt bleibt offen – weiter zum nächsten. */
  spaeter(): void {
    if (this.konflikte.length <= 1) {
      this.dialogRef.close();
      return;
    }

    this.naechster();
  }

  // ── QR ──

  /** Die lokale Zuordnung zurücknehmen; der Sticker bleibt dort, wo er am Server hängt. */
  async qrZuruecknehmen(): Promise<void> {
    await this.entscheide(k => this.sync.qrZuordnungZuruecknehmen(k.id).then(() => undefined));
  }

  /** Lokale Zuordnung zurücknehmen und zur Messstelle wechseln, um einen anderen Code zu scannen. */
  async qrAnderenCode(): Promise<void> {
    const k = this.aktuell;

    if (!k || this.laufend) {
      return;
    }

    this.laufend = true;

    try {
      const messstelleId = await this.sync.qrZuordnungZuruecknehmen(k.id);

      if (messstelleId != null) {
        this.dialogRef.close({ navigiereZuMessstelle: messstelleId });
        return;
      }
    } finally {
      this.laufend = false;
    }

    await this.laden();
  }

  /** „Messstelle hat schon einen Code": der gescannte Code ersetzt den vorhandenen. */
  async qrErsetzen(): Promise<void> {
    await this.entscheide(k => this.sync.qrVorhandenenErsetzen(k.id));
  }

  private async entscheide(aktion: (k: SyncKonfliktEintrag) => Promise<void>): Promise<void> {
    const k = this.aktuell;

    if (!k || this.laufend) {
      return;
    }

    this.laufend = true;

    try {
      await aktion(k);
    } finally {
      this.laufend = false;
    }

    await this.laden();
  }

  // ===================================================================
  // ABGELEHNTE ÄNDERUNGEN
  // ===================================================================

  /** Verwirft die endgültig abgelehnten Änderungen – ausdrücklich vom Benutzer angestoßen. */
  async verwerfeFehlgeschlagene(): Promise<void> {
    const anzahl = this.fehlgeschlagen.length;

    if (anzahl === 0) {
      return;
    }

    const frage =
      anzahl === 1
        ? 'Diese Änderung endgültig verwerfen? Sie wird nicht mehr übertragen.'
        : `Diese ${anzahl} Änderungen endgültig verwerfen? Sie werden nicht mehr übertragen.`;

    if (!confirm(frage)) {
      return;
    }

    await this.sync.verwerfeFehlgeschlagene();
    await this.laden();
  }

  /** Stellt die abgelehnten Änderungen erneut ein – etwa nach wiederhergestellter Berechtigung. */
  async wiederholeFehlgeschlagene(): Promise<void> {
    if (this.fehlgeschlagen.length === 0 || this.laufend) {
      return;
    }

    this.laufend = true;

    try {
      await this.sync.wiederholeFehlgeschlagene();
    } finally {
      this.laufend = false;
    }

    await this.laden();
  }

  // ===================================================================
  // NAVIGATION UND ANZEIGE
  // ===================================================================

  vorheriger(): void {
    if (this.konflikte.length === 0) {
      return;
    }

    this.index = (this.index - 1 + this.konflikte.length) % this.konflikte.length;
    this.feldwahlZuruecksetzen();
  }

  naechster(): void {
    if (this.konflikte.length === 0) {
      return;
    }

    this.index = (this.index + 1) % this.konflikte.length;
    this.feldwahlZuruecksetzen();
  }

  /** Lesbare Bezeichnung einer Änderungsart. */
  typText(typ: SyncTyp): string {
    switch (typ) {
      case 'PRUEFPUNKT_ERGEBNIS':
        return 'Prüfergebnis';

      case 'PRUEFPUNKT_ANLEGEN':
        return 'Prüfpunkt anlegen';

      case 'PRUEFPUNKT_LOESCHEN':
        return 'Prüfpunkt löschen';

      case 'MESSSTELLE_PRUEFERGEBNIS':
        return 'Gesamtergebnis';

      case 'MESSSTELLE_NOTIZ':
        return 'Notiz zur Messstelle';

      case 'MESSSTELLE_SIGNALSTATUS':
        return 'Signalstatus';

      case 'MESSSTELLE_KATEGORIE':
        return 'Kategorie';

      case 'MESSSTELLE_WERTE':
        return 'Allgemeine Daten';

      case 'SPS_PRUEFSTATUS':
        return 'SPS-Prüfstatus';

      case 'QR_ZUORDNEN':
        return 'QR-Code-Zuordnung';

      default:
        return typ;
    }
  }

  trackByFehler(_index: number, eintrag: SyncQueueEintrag): string {
    return eintrag.id;
  }

  trackByZeile(_index: number, zeile: Vergleichszeile): string {
    return zeile.schluessel;
  }

  trackByFeld(_index: number, feld: SyncKonfliktFeld): string {
    return feld.schluessel;
  }

  schliessen(): void {
    this.dialogRef.close();
  }
}
